import { collection, doc, getDoc, getDocFromServer, getDocs, limit, orderBy, query, runTransaction, serverTimestamp, setDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { sortSessionsByRecency } from "./sessionOrdering";
import { compactTranscriptFingerprint } from "./transcriptVersion";
import { hasMeaningfulCompletionTranscript } from "./sessionCompletion";

const GLOBAL_SESS_KEY = "nia_sessions";
const orgKey = (orgId) => `${GLOBAL_SESS_KEY}__org_${orgId || "org_default"}`;
export const getSessionUserStorageKey = (uid, orgId) => (
  uid ? `${orgKey(orgId)}__uid_${uid}` : ""
);
const orgIdFor = (user, override) => override || user?.orgId || "org_default";
const canUseOrgSessionCache = (user = {}) => (
  ["admin", "coach", "superadmin", "investor"].includes(String(user?.role || "").toLowerCase())
);

function activeCoachingState(value = {}) {
  const state = value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
  return {
    ...state,
    sessionComplete: false,
    completionReason: "",
    completionAction: "",
  };
}

function clientDraftSummary(value = {}, { sessionComplete = false } = {}) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const summary = {
    ...value,
    // A browser may persist a working summary, but only the trusted finalizer
    // may promote it to a canonical final/reviewed state.
    generationStatus: "draft",
    status: "draft",
    canonical: false,
  };
  if (!sessionComplete) {
    summary.coachingState = activeCoachingState(summary.coachingState);
  }
  return summary;
}

export function filterCachedSessionsForViewer(records = [], user = {}) {
  const rows = Array.isArray(records) ? records : [];
  if (canUseOrgSessionCache(user)) return rows;
  const uid = String(user?.uid || "");
  if (!uid) return [];
  return rows.filter((record) => String(record?.userId || "") === uid);
}

function canUseStorage() {
  return typeof window !== "undefined" && window.localStorage;
}

function writeLocal(key, value) {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function readLocal(key) {
  if (!canUseStorage()) return [];
  try {
    const raw = JSON.parse(window.localStorage.getItem(key) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

export function normalizeSessionForFirestore(record, user, resolvedOrgId) {
  const createdAt = record.createdAt || record.date || Date.now();
  const storedUserId = record.userId || user?.uid || null;
  const isOwnerWrite = user?.role === "member" && String(storedUserId || "") === String(user?.uid || "");
  const storedUserEmail = String(record.userEmail || "").trim().toLowerCase();
  const payload = {
    ...record,
    id: record.id || createdAt,
    createdAt,
    ...(record.lastMessageAt ? { lastMessageAt: record.lastMessageAt } : {}),
    userId: storedUserId,
    userEmail: storedUserEmail || (isOwnerWrite ? user?.email || null : null),
    role: record.role || user?.role || "member",
    orgId: resolvedOrgId || record.orgId || user?.orgId || "org_default",
    updatedAt: serverTimestamp(),
  };

  // The rules reserve completionVersion for an immutable, validated completion.
  // Active records created by the chat UI historically carried a null placeholder,
  // which is observably different from an absent key in Firestore rules.
  if (
    payload.sessionComplete !== true ||
    !payload.completionVersion ||
    typeof payload.completionVersion !== "object" ||
    Array.isArray(payload.completionVersion)
  ) {
    delete payload.completionVersion;
  }

  if (payload.sessionComplete !== true) {
    payload.sessionComplete = false;
    payload.endedAt = null;
    payload.completionReason = "";
    payload.completionAction = "";
    if (payload.coachingState) {
      payload.coachingState = activeCoachingState(payload.coachingState);
    }
    if (payload.sessionSummary) {
      payload.sessionSummary = clientDraftSummary(payload.sessionSummary);
    }
  }

  return payload;
}

export async function writeSessionToFirestore(record, user, orgIdOverride) {
  if (!user?.uid || user?.role === "investor") return false;
  if (
    user?.role === "member" &&
    String(record?.userId || "") &&
    String(record.userId) !== String(user.uid)
  ) {
    return false;
  }
  try {
    const orgId = orgIdFor(user, orgIdOverride);
    const payload = normalizeSessionForFirestore(record, user, orgId);
    const hasTrustedContinuation = Boolean(payload.postSessionContinuation);
    delete payload.postSessionContinuation;
    if (hasTrustedContinuation) {
      delete payload.sessionSummary;
      delete payload.summaryHistory;
      delete payload.detectedThemes;
      delete payload.suggestedNextStep;
      delete payload.nextFollowUpFocus;
      delete payload.followUpRecommendation;
      delete payload.followUpHistory;
      delete payload.summaryDelivery;
    }
    const ref = doc(db, "orgs", orgId, "sessions", String(payload.id));
    await setDoc(ref, payload, { merge: true });
    return true;
  } catch {
    // allow local fallback silently
    return false;
  }
}

export async function confirmOwnedSessionReadableFromServer(
  sessionId,
  user,
  orgIdOverride
) {
  const safeSessionId = String(sessionId || "").trim();
  const userId = String(user?.uid || "").trim();
  const orgId = String(orgIdFor(user, orgIdOverride) || "").trim();
  if (!safeSessionId || !userId || !orgId) return false;
  try {
    const ref = doc(db, "orgs", orgId, "sessions", safeSessionId);
    const snapshot = await getDocFromServer(ref);
    const record = snapshot.exists() ? snapshot.data() || {} : null;
    return Boolean(
      record &&
      String(record.userId || "") === userId &&
      String(record.orgId || "") === orgId
    );
  } catch {
    return false;
  }
}

export async function writeSessionCompletionToFirestore(
  record,
  user,
  orgIdOverride
) {
  const completion =
    record?.completionVersion && typeof record.completionVersion === "object"
      ? record.completionVersion
      : null;
  const messages = Array.isArray(record?.messages) ? record.messages : [];
  const orgId = orgIdFor(user, orgIdOverride);
  const sessionId = String(record?.id || "").trim();
  const userId = String(record?.userId || "").trim();
  if (
    !completion ||
    !user?.uid ||
    String(user?.role || "").toLowerCase() !== "member" ||
    !sessionId ||
    !orgId ||
    userId !== String(user.uid) ||
    record.sessionComplete !== true ||
    String(completion.id || "").trim() === "" ||
    String(completion.orgId || "") !== String(orgId) ||
    String(completion.userId || "") !== userId ||
    String(completion.sessionId || "") !== sessionId ||
    !hasMeaningfulCompletionTranscript(messages) ||
    Number(completion.sourceMessageCount) !== messages.length ||
    String(completion.transcriptFingerprint || "") !==
      compactTranscriptFingerprint(messages) ||
    String(completion.completedAt || "") !== String(record.endedAt || "")
  ) {
    return false;
  }

  try {
    const ref = doc(db, "orgs", orgId, "sessions", sessionId);
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(ref);
      const current = snap.exists() ? snap.data() || {} : {};
      const currentOwner = String(current.userId || "").trim();
      const currentVersionId = String(current?.completionVersion?.id || "").trim();
      if (
        (currentOwner && currentOwner !== userId) ||
        (currentVersionId && currentVersionId !== String(completion.id))
      ) {
        throw new Error("completion_boundary_changed");
      }
      if (
        currentVersionId === String(completion.id) &&
        current.sessionComplete === true
      ) {
        return;
      }

      const completionReason = String(record.completionReason || "");
      const completionAction = String(record.completionAction || "");
      const sourceCoachingState =
        record?.coachingState && typeof record.coachingState === "object"
          ? record.coachingState
          : record?.sessionSummary?.coachingState || {};
      const coachingState = {
        ...sourceCoachingState,
        sessionComplete: true,
        completionReason,
        completionAction,
      };
      const hasTrustedServerContinuation = Boolean(
    current?.postSessionContinuation &&
    typeof current.postSessionContinuation === "object"
  );

  const completionWrite = {
    id: sessionId,
    orgId,
    userId,
    createdAt: record.createdAt || record.date || Date.now(),
    startedAt: record.startedAt || record.createdAt || record.date || Date.now(),
    lastMessageAt: record.lastMessageAt || completion.completedAt,
    messages,
    messageCount: messages.length,
    sessionComplete: true,
    completionReason,
    completionAction,
    completionVersion: completion,
    endedAt: completion.completedAt,
    coachingState,
    updatedAt: serverTimestamp(),
  };

  if (!hasTrustedServerContinuation) {
    completionWrite.sessionSummary = clientDraftSummary({
      ...(record.sessionSummary || {}),
      coachingState,
    }, { sessionComplete: true });
  }

  transaction.set(ref, completionWrite, { merge: true });
    });
    return true;
  } catch {
    return false;
  }
}

export async function writeSessionFieldsToFirestore(
  sessionId,
  fields,
  user,
  orgIdOverride
) {
  const safeSessionId = String(sessionId || "").trim();
  if (
    !safeSessionId ||
    !user?.uid ||
    user?.role === "investor" ||
    !fields ||
    typeof fields !== "object" ||
    Array.isArray(fields)
  ) {
    return false;
  }
  const allowedFields = new Set([
    "sessionSummary",
    "summaryHistory",
    "summaryDelivery",
    "followUp",
    "followUpHistory",
    "followUpRecommendation",
    "supportReview",
    "sessionReengagementEligibleAt",
  ]);
  const patch = Object.entries(fields).reduce((result, [key, value]) => {
    if (allowedFields.has(key) && value !== undefined) {
      result[key] = key === "sessionSummary"
        ? clientDraftSummary(value)
        : value;
    }
    return result;
  }, {});
  if (!Object.keys(patch).length) return false;
  try {
    const orgId = orgIdFor(user, orgIdOverride);
    const ref = doc(db, "orgs", orgId, "sessions", safeSessionId);
    await setDoc(ref, {
      ...patch,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return true;
  } catch {
    return false;
  }
}

export async function syncSessionsFromFirestore(user, orgIdOverride, onError) {
  if (!user?.uid) return;
  try {
    const orgId = orgIdFor(user, orgIdOverride);
    const base = collection(db, "orgs", orgId, "sessions");
    const q =
      user.role === "admin" || user.role === "coach" || user.role === "superadmin" || user.role === "investor"
        ? query(base, orderBy("createdAt", "desc"), limit(500))
        : query(base, where("userId", "==", user.uid), orderBy("createdAt", "desc"), limit(500));

    const snap = await getDocs(q);
    const rows = sortSessionsByRecency(snap.docs.map((docSnap) => docSnap.data()));
    if (canUseOrgSessionCache(user)) {
      writeLocal(orgKey(orgId), rows);
    } else {
      const key = getSessionUserStorageKey(user.uid, orgId);
      if (key) writeLocal(key, filterCachedSessionsForViewer(rows, user));
    }
  } catch (err) {
    if (onError) onError(err);
    // ignore and keep localStorage data
  }
}

export async function syncLocalSessionsToFirestore(user, orgIdOverride, onError) {
  if (!user?.uid || user?.role === "investor") return;
  const orgId = orgIdFor(user, orgIdOverride);
  const local = canUseOrgSessionCache(user)
    ? readLocal(orgKey(orgId))
    : filterCachedSessionsForViewer(
        readLocal(getSessionUserStorageKey(user.uid, orgId)),
        user
      );
  if (!local.length) return;
  try {
    await Promise.all(local.slice(0, 200).map(async (record) => {
      const sessionId = String(record?.id || "").trim();
      if (!sessionId) return false;
      const ref = doc(db, "orgs", orgId, "sessions", sessionId);
      const existing = await getDoc(ref);
      if (existing.exists()) return false;
      return writeSessionToFirestore(record, user, orgIdOverride);
    }));
  } catch (err) {
    if (onError) onError(err);
  }
}
